import React, { useRef } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import { useReadOnly, useSelected, useSlate, ReactEditor } from 'slate-react';
import { Transforms } from 'slate';
import type {
  ComponentProps,
  LinkCardElement,
} from '../../../types/aha-editor';
import useAhaEditorFocused from '../../../components/useAhaEditorFocused';
import Resizer from '../../../components/Resizer/Resizer';
import SelectionRectangle from '../../../components/SelectionRectangle/SelectionRectangle';
import styles from './LinkCardComponent.module.css';
import { useLinkHandlers, getLinkAttributes } from '../../LinkPlugin/utils';
import MediaCommon from '../../MediaPlugin/MediaCommon';
import classNames from 'classnames';
import { DEFAULT_WIDTH, DEFAULT_MIN_WIDTH } from '../constants';
import { updateLinkCardProperty } from '../utils';
import MissingImageIcon from '../../../plugins/MediaPlugin/components/MissingImageIcon';

const LinkCardComponent: React.FC<ComponentProps<LinkCardElement>> = ({
  attributes,
  element,
  children,
}) => {
  const editor = useSlate();
  const selected = useSelected();
  const readOnly = useReadOnly();
  const focused = useAhaEditorFocused();
  const keepRatio = Boolean(element.linkCard_keepRatio);

  const linkCardAnchorRef = useRef<HTMLAnchorElement>(null);

  const selectNode = (event: React.MouseEvent) => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.select(editor, { path: [...path, 0], offset: 0 });
    event.preventDefault();
  };

  const href = element.linkCard_href || '#';
  const linkTarget = element.linkCard_target;
  const fullWidth = element.linkCard_fullWidth === true;
  const shouldRenderAsLink = href && href !== '#';

  const { handleMouseDown, handleClick } = useLinkHandlers({
    href,
    linkTarget,
    focused,
    readOnly,
  });

  const extraAttrs = getLinkAttributes(href, linkTarget);

  // Handle image display
  let imageUrl: string | undefined;
  if (element.linkCard_image) {
    if (MediaCommon.isTemporaryDataUrl(element.linkCard_image)) {
      imageUrl = MediaCommon.getTemporaryData(element.linkCard_image);
    } else {
      imageUrl = element.linkCard_image;
    }
  }

  let image;
  switch (element.linkCard_image) {
    case undefined:
      // When the image is not set, show a placeholder.
      image = (
        <MissingImageIcon
          width={200}
          height={200}
          className={classNames(styles.linkCardImageElement, {
            [styles.linkCardImageKeepRatio]: keepRatio,
          })}
          borderWidth={0}
        />
      );
      break;
    case '':
      // When the image is explicitly set to an empty string (indicating removal), show nothing.
      image = null;
      break;
    default:
      // When the image is set to a valid URL, show the image.
      image = (
        <img
          src={imageUrl}
          alt={[element.linkCard_title, element.linkCard_description]
            .filter(Boolean)
            .join(': ')}
          className={classNames(styles.linkCardImageElement, {
            [styles.linkCardImageKeepRatio]: keepRatio,
          })}
        />
      );
  }

  const cardContent = (
    <span
      className={styles.linkCardLayout}
      style={{
        textAlign: element.linkCard_alignment,
      }}
    >
      {image && <span className={styles.linkCardImage}>{image}</span>}
      <span
        className={styles.linkCardContent}
        onClick={!selected ? selectNode : undefined}
      >
        <span className={styles.linkCardTitle}>
          {element.linkCard_title ?? 'Title'}
        </span>

        <span className={styles.linkCardDescription}>
          {element.linkCard_description ?? 'Description'}
        </span>

        {element.linkCard_href && element.linkCard_linkText && (
          <span className={styles.linkCardUrl}>
            <span className={styles.linkCardUrlText}>
              {element.linkCard_linkText}
            </span>
            <i className="fa-regular fa-arrow-right" />
          </span>
        )}

        {children}
      </span>
    </span>
  );

  return (
    <span
      {...attributes}
      className={classNames(styles.linkCard, {
        [styles.linkCardVertical]: element.linkCard_orientation === 'vertical',
        [styles.linkCardFullWidth]: fullWidth,
      })}
      contentEditable={false}
    >
      <Resizer
        width={element.resizer_width}
        originalWidth={element.resizer_originalWidth ?? DEFAULT_WIDTH}
        element={element}
        handles={selected && focused && !readOnly ? ['e'] : []}
        keepRatio={keepRatio}
        widthOnly={true}
        showPixelSize={true}
        liveResize={true}
        minWidth={DEFAULT_MIN_WIDTH}
        fullWidth={fullWidth}
        moveStartCallback={() => {
          updateLinkCardProperty(editor, element, 'linkCard_fullWidth', false);
        }}
      >
        <SelectionRectangle inline={fullWidth} hasMargin={false} fill>
          {shouldRenderAsLink ? (
            <a
              ref={linkCardAnchorRef}
              className={styles.linkCardAnchor}
              href={href}
              {...extraAttrs}
              onMouseDown={handleMouseDown}
              onClick={handleClick}
            >
              {cardContent}
            </a>
          ) : (
            <span
              className={styles.linkCardAnchor}
              onClick={!selected ? selectNode : undefined}
            >
              {cardContent}
            </span>
          )}
        </SelectionRectangle>
      </Resizer>
    </span>
  );
};

export default ApplicationView(LinkCardComponent);
